import { playEchoSafe } from './echoSafeOutput';
import { AUDIO_SAMPLE_RATE, decodePcm16, hasVoice } from './pcm';

/**
 * Gapless playback of the model's audio frames.
 *
 * Frames are *scheduled* rather than played on arrival: each buffer starts
 * exactly where the previous one ends, on the AudioContext's own clock, so
 * network jitter does not leave audible seams; a jitter buffer absorbs stalls.
 * GPT-Live handles barge-in itself (it goes silent), so nothing is flushed;
 * closing the context on hang-up stops whatever is scheduled.
 */

/**
 * Jitter buffer. GPT-Live streams in real time (one 100 ms frame per 100 ms),
 * and every frame is scheduled from the main thread, so a main-thread stall
 * (a screen capture, a heavy render) delays frames. Starting playback this
 * far ahead of the clock, and again whenever it runs dry, lets a stall up to
 * this long pass without a gap, for this much added latency.
 */
const JITTER_BUFFER_SECONDS = 0.3;
/** Below this much queued audio the next frame restarts the buffer. */
const UNDERRUN_SECONDS = 0.02;

export class PlaybackQueue {
  private context: AudioContext | null = null;
  /** Every frame plays through this node, so one analyser sees all output. */
  private bus: GainNode | null = null;
  private stopOutput: (() => void) | null = null;
  /** When the next buffer should start, on the context clock. */
  private cursor = 0;

  /**
   * The context and the node all playback flows through, for visualizers.
   * Created on first use, which is the click that starts the call, so the
   * context is allowed to play.
   */
  output(): { context: AudioContext; node: AudioNode } {
    if (!this.context || !this.bus) {
      this.context = new AudioContext({ sampleRate: AUDIO_SAMPLE_RATE });
      this.bus = this.context.createGain();
      this.stopOutput = playEchoSafe(this.context, this.bus);
    }
    return { context: this.context, node: this.bus };
  }

  /**
   * A short rising two-note chime: the call is connected. Played through the
   * same bus, so the waveform shows it too.
   */
  chime(): void {
    const { context, node } = this.output();
    void context.resume().catch(() => undefined);
    const notes = [
      { hz: 660, at: 0 },
      { hz: 880, at: 0.12 },
    ];
    for (const { hz, at } of notes) {
      const start = context.currentTime + 0.02 + at;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = hz;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.15, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.18);
      oscillator.connect(gain).connect(node);
      oscillator.start(start);
      oscillator.stop(start + 0.2);
    }
  }

  /** The playback clock, in seconds. */
  get currentTime(): number {
    return this.context?.currentTime ?? 0;
  }

  /**
   * Queue one base64 PCM16 frame from the model.
   *
   * @returns when the frame starts playing on {@link currentTime}'s clock,
   *   how long it lasts and whether it is louder than silence, or null for an
   *   empty frame.
   */
  enqueue(
    base64: string,
  ): { playsAt: number; seconds: number; audible: boolean } | null {
    const samples = decodePcm16(base64);
    if (samples.length === 0) return null;
    const { context, node } = this.output();
    // Autoplay policy can leave the context suspended until a gesture; the session
    // always starts from a click, so this resolves immediately in practice.
    void context.resume().catch(() => undefined);

    const buffer = context.createBuffer(1, samples.length, AUDIO_SAMPLE_RATE);
    buffer.copyToChannel(samples, 0);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(node);

    const startAt =
      this.cursor - context.currentTime < UNDERRUN_SECONDS
        ? context.currentTime + JITTER_BUFFER_SECONDS
        : this.cursor;
    source.start(startAt);
    this.cursor = startAt + buffer.duration;
    return {
      playsAt: startAt,
      seconds: buffer.duration,
      audible: hasVoice(samples),
    };
  }

  /** Release the audio device; scheduled audio stops with the context. */
  close(): void {
    this.stopOutput?.();
    this.stopOutput = null;
    void this.context?.close().catch(() => undefined);
    this.context = null;
    this.bus = null;
    this.cursor = 0;
  }
}
